import { useCallback, useEffect, useReducer, useRef } from 'react';

import {
  type DropOutcome,
  boardReducer,
  createBoardState,
  dropOutcome,
} from '../core/boardReducer';
import type { Deal } from '../core/deal';
import type { BoardState, GridConfig } from '../core/types';

/**
 * Legacy `incorrectAnswer` tweens the rejected tile's alpha to 0 over 800ms
 * and only then resets (`games/engine/boardGameEngine.ts:779-794`).
 */
export const REJECT_MS = 800;

export interface UseVifBoardOptions {
  config: GridConfig;
  /**
   * The board to show. A new object means "deal this" - the owner draws a
   * board per legacy `nextProblem()` and this hook renders whatever it is
   * handed, so there is exactly one place that decides what gets dealt.
   */
  deal: Deal;
  /** Every cell resolved. Legacy `checkForEnd` win branch. */
  onCleared?: () => void;
  /** The wrong-answer fade finished. Legacy `resetGame`. */
  onRejectSettled?: () => void;
}

export interface UseVifBoard {
  state: BoardState;
  drop: (from: number, to: number) => DropOutcome | null;
}

/**
 * Owns board state, the rejection timer, and the completion signal.
 *
 * It deliberately does not draw boards. A drawn board also has to repopulate
 * the variant's problem pool, record the draw against the layer, and reset the
 * round clock - all of which legacy does in one `nextProblem()` call - so
 * `useRulesBoardGame` performs the draw and passes the result down as `deal`.
 */
export function useVifBoard({
  config,
  deal,
  onCleared,
  onRejectSettled,
}: UseVifBoardOptions): UseVifBoard {
  const [state, dispatch] = useReducer(boardReducer, undefined, () =>
    createBoardState(config, deal),
  );

  // Kept current so `drop` can compute the outcome against the latest state
  // without taking a dependency that would change its identity every drop.
  const stateRef = useRef(state);
  stateRef.current = state;

  const drop = useCallback((from: number, to: number) => {
    const outcome = dropOutcome(stateRef.current, from, to);
    if (outcome === null) {
      return null;
    }
    dispatch({ type: 'DROP', from, to });
    return outcome;
  }, []);

  // Adopt each new deal. Compared by identity against the last one adopted so
  // an unrelated re-render cannot reshuffle a board mid-solve.
  const dealtRef = useRef(deal);
  useEffect(() => {
    if (dealtRef.current === deal) {
      return;
    }
    dealtRef.current = deal;
    dispatch({ type: 'DEAL', ...deal });
  }, [deal]);

  // The wrong-answer fade. The timer is cleared on unmount and whenever the
  // rejection is superseded, so a board that unmounts mid-reject never calls
  // back into a dead tree.
  const rejectSettledRef = useRef(onRejectSettled);
  rejectSettledRef.current = onRejectSettled;

  useEffect(() => {
    if (state.rejectingIndex === null) {
      return;
    }

    const timer = setTimeout(() => {
      rejectSettledRef.current?.();
    }, REJECT_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [state.rejectingIndex]);

  // Fire completion once per completed board.
  const clearedRef = useRef(false);
  useEffect(() => {
    if (state.complete && !clearedRef.current) {
      clearedRef.current = true;
      onCleared?.();
    } else if (!state.complete) {
      clearedRef.current = false;
    }
  }, [state.complete, onCleared]);

  return { state, drop };
}
